import { placarDoPeriodo, type FichaDaTemporada, type LinhaDaTemporada } from "./temporada.js";
import type { DayKey } from "./types.js";

/* ============================================================
   Resumo do mês e retrospectiva do ano

   Montado UMA vez, pelo servidor, quando o período fecha, e guardado
   pronto: abre na hora e não muda depois. A retrospectiva leria
   trezentos pratos por pessoa se fosse calculada ao abrir.

   Tudo aqui é puro: recebe os pratos, as fichas e as conquistas do
   período e devolve os cartões. Só usa o que o grupo já vê — pratos do
   grupo — e o pessoal só a própria pessoa lê (regra do Firestore).

   Os cuidados que vieram de olhar o painel:
   - ingrediente vem de texto da IA: "Arroz branco", "arroz" e "feijao"
     sem acento viram a mesma linha, com o nome bonito;
   - ingrediente só existe em prato com estimativa, então o texto diz
     "em 71 dos 183 pratos", nunca "o grupo comeu";
   - lugar só com 2 visitas ou mais e que não seja endereço de rua (o
     seletor pega o ponto mais próximo: banco, Poupatempo, "Rua X, 120").
   ============================================================ */

export type TipoDeResumo = "mes" | "ano";

/** "2026-09" → mês; "2026" → ano. */
export function janelaDoResumo(periodo: string): { tipo: TipoDeResumo; de: DayKey; ate: DayKey } | null {
  if (/^\d{4}-\d{2}$/.test(periodo)) {
    const [a, m] = periodo.split("-").map(Number);
    if (!a || !m || m < 1 || m > 12) return null;
    const ultimo = new Date(Date.UTC(a, m, 0)).getUTCDate();
    return { tipo: "mes", de: `${periodo}-01`, ate: `${periodo}-${String(ultimo).padStart(2, "0")}` };
  }
  if (/^\d{4}$/.test(periodo)) return { tipo: "ano", de: `${periodo}-01-01`, ate: `${periodo}-12-31` };
  return null;
}

/** O período anterior, para a comparação ("setembro" → "agosto"). */
export function periodoAnterior(periodo: string): string {
  if (/^\d{4}$/.test(periodo)) return String(Number(periodo) - 1);
  const [a, m] = periodo.split("-").map(Number);
  const d = new Date(Date.UTC(a!, m! - 2, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

/** O mês ou ano que acabou de fechar em `hoje` (dia 1 → o mês anterior). */
export function periodosQueFecharam(hoje: DayKey): string[] {
  const [a, m, d] = hoje.split("-").map(Number);
  if (d !== 1) return [];
  const mes = periodoAnterior(`${a}-${String(m).padStart(2, "0")}`);
  return m === 1 ? [mes, String(a! - 1)] : [mes];
}

const MESES = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho",
  "agosto", "setembro", "outubro", "novembro", "dezembro"];

/** "setembro" · "setembro de 2025" (se não for o ano de `hoje`) · "2025". */
export function nomeDoPeriodo(periodo: string, anoAtual?: number): string {
  if (/^\d{4}$/.test(periodo)) return periodo;
  const [a, m] = periodo.split("-").map(Number);
  const nome = MESES[(m ?? 1) - 1] ?? periodo;
  return anoAtual && a !== anoAtual ? `${nome} de ${a}` : nome;
}

/* ---------- ingrediente ---------- */

const semAcento = (s: string) => s.normalize("NFD").replace(/\p{Diacritic}/gu, "");

/** Palavras que descrevem o preparo, não o ingrediente. */
const PREPARO = new Set([
  "branco", "branca", "cozido", "cozida", "cozidos", "cozidas", "grelhado", "grelhada",
  "frito", "frita", "fritos", "fritas", "assado", "assada", "refogado", "refogada",
  "temperado", "temperada", "carioca", "preto", "cozinhado", "picado", "picada",
  "natural", "caseiro", "caseira", "simples", "puro", "pura",
]);

/**
 * A chave que junta as variações: minúsculas, sem acento, sem as
 * palavras de preparo e sem o que vem entre parênteses.
 * "Arroz branco cozido" → "arroz"; "Feijão (carioca)" → "feijao".
 */
export function chaveDoIngrediente(nome: string): string {
  const base = semAcento(String(nome ?? "").toLowerCase())
    .replace(/\(.*?\)/g, " ").replace(/[^a-z0-9 ]+/g, " ");
  const palavras = base.split(/\s+/).filter((p) => p && !PREPARO.has(p) && p !== "de" && p !== "com");
  return palavras.join(" ").trim();
}

/** O nome bonito: o mais curto que tem acento, senão o mais curto, com inicial maiúscula. */
function nomeBonito(variantes: string[]): string {
  const limpas = variantes.map((v) => v.replace(/\(.*?\)/g, "").replace(/\s+/g, " ").trim()).filter(Boolean);
  const comAcento = limpas.filter((v) => semAcento(v) !== v);
  const escolhido = [...(comAcento.length ? comAcento : limpas)]
    .sort((a, b) => a.length - b.length)[0] ?? "";
  const curto = escolhido.split(/\s+/).filter((p) => !PREPARO.has(semAcento(p.toLowerCase()))).join(" ") || escolhido;
  return curto.charAt(0).toUpperCase() + curto.slice(1).toLowerCase();
}

export interface IngredienteDoResumo { nome: string; pratos: number; comEstimativa: number }

/** O ingrediente que mais apareceu, contado por PRATO (não por item). */
export function ingredienteDoPeriodo(pratos: readonly { itens: readonly string[] }[]): IngredienteDoResumo | null {
  const comEstimativa = pratos.filter((p) => p.itens.length > 0);
  const conta = new Map<string, { n: number; variantes: string[] }>();
  for (const p of comEstimativa) {
    const vistos = new Set<string>();
    for (const item of p.itens) {
      const k = chaveDoIngrediente(item);
      if (!k || vistos.has(k)) continue;
      vistos.add(k);
      const c = conta.get(k) ?? { n: 0, variantes: [] };
      c.n += 1;
      c.variantes.push(item);
      conta.set(k, c);
    }
  }
  const top = [...conta.values()].sort((a, b) => b.n - a.n)[0];
  if (!top || top.n < 2) return null;
  return { nome: nomeBonito(top.variantes), pratos: top.n, comEstimativa: comEstimativa.length };
}

/* ---------- lugar ---------- */

/** "Rua das Flores, 120", "Av. Paulista 1000", "R. X" — endereço, não lugar. */
export function pareceEndereco(nome: string): boolean {
  const n = semAcento(nome.trim().toLowerCase());
  return /^(rua|r\.|av\.?|avenida|alameda|al\.|travessa|tv\.|rodovia|estrada|praca|largo|viela)\b/.test(n)
    || /,\s*\d+/.test(n) || /\b\d{3,}\b/.test(n);
}

export interface LugarDoResumo { nome: string; visitas: number }

export function lugarDoPeriodo(pratos: readonly { place: string; placeKey: string; homemade: boolean }[]): LugarDoResumo | null {
  const conta = new Map<string, { nome: string; n: number }>();
  for (const p of pratos) {
    if (p.homemade || !p.placeKey || !p.place || pareceEndereco(p.place)) continue;
    const c = conta.get(p.placeKey) ?? { nome: p.place.trim(), n: 0 };
    c.n += 1;
    conta.set(p.placeKey, c);
  }
  const top = [...conta.values()].sort((a, b) => b.n - a.n)[0];
  return top && top.n >= 2 ? { nome: top.nome, visitas: top.n } : null;
}

/* ---------- o resumo ---------- */

export interface PratoDoResumo {
  id: string;
  uid: string;
  authorName: string;
  title: string;
  day: DayKey;
  /** ms. */
  at: number;
  homemade: boolean;
  price: number | null;
  place: string;
  placeKey: string;
  cuisineId: string | null;
  itens: string[];
  ratingAvg: number | null;
  ratingCount: number;
  thumbPath: string;
}

export interface MissaoFechada { uid: string; nome: string; emoji: string; bonus: number; day: DayKey }

export interface CartaoDePrato {
  id: string; uid: string; authorName: string; title: string; thumbPath: string;
  nota: number | null; notas: number;
}

export interface ResumoDoGrupo {
  periodo: string;
  tipo: TipoDeResumo;
  de: DayKey;
  ate: DayKey;
  pratos: number;
  pessoas: number;
  diasAtivos: number;
  cozinhados: number;
  comprados: number;
  ingrediente: IngredienteDoResumo | null;
  cozinhas: { id: string; label: string; emoji: string; n: number }[];
  lugar: LugarDoResumo | null;
  curiosidades: string[];
  pratoDoPeriodo: CartaoDePrato | null;
  missoes: { total: number; porMissao: { nome: string; emoji: string; n: number }[]; preferida: string | null };
  podio: LinhaDaTemporada[];
}

export interface ResumoPessoal {
  periodo: string;
  uid: string;
  pratos: number;
  pontos: number;
  ingrediente: IngredienteDoResumo | null;
  casa: number;
  rua: number;
  gasto: number;
  comPreco: number;
  melhorPrato: CartaoDePrato | null;
  missoes: { nome: string; emoji: string; bonus: number }[];
  quaseLa: { nome: string; emoji: string; feitos: number; need: number } | null;
  anterior: { pratos: number; pontos: number } | null;
  posicao: number;
}

const DIAS = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];
const horaBrasilia = (ms: number) => (new Date(ms).getUTCHours() + 21) % 24;
const dentro = (de: DayKey, ate: DayKey) => (d: DayKey) => d >= de && d <= ate;

function cartao(p: PratoDoResumo): CartaoDePrato {
  return {
    id: p.id, uid: p.uid, authorName: p.authorName, title: p.title, thumbPath: p.thumbPath,
    nota: p.ratingAvg, notas: p.ratingCount,
  };
}

/** Prato com a melhor nota (2+ notas). `null` sem notas no desafio. */
function melhorPorNota(pratos: readonly PratoDoResumo[], comNotas: boolean): CartaoDePrato | null {
  if (!comNotas) return null;
  const top = pratos.filter((p) => p.ratingCount >= 2 && p.ratingAvg != null)
    .sort((a, b) => (b.ratingAvg ?? 0) - (a.ratingAvg ?? 0) || b.ratingCount - a.ratingCount)[0];
  return top ? cartao(top) : null;
}

function curiosidades(pratos: readonly PratoDoResumo[]): string[] {
  const frases: string[] = [];
  if (!pratos.length) return frases;
  const porDia = new Array(7).fill(0) as number[];
  for (const p of pratos) {
    const [a, m, d] = p.day.split("-").map(Number);
    porDia[new Date(Date.UTC(a!, m! - 1, d!)).getUTCDay()]! += 1;
  }
  const dia = porDia.indexOf(Math.max(...porDia));
  frases.push(`O dia mais animado foi ${DIAS[dia]}: ${porDia[dia]} pratos.`);
  const caro = pratos.filter((p) => (p.price ?? 0) > 0).sort((a, b) => (b.price ?? 0) - (a.price ?? 0))[0];
  if (caro) frases.push(`O prato mais caro foi ${caro.title || "um prato"} de ${caro.authorName.split(" ")[0]}: R$ ${caro.price!.toFixed(2).replace(".", ",")}.`);
  const tarde = pratos.filter((p) => p.at && (horaBrasilia(p.at) >= 23 || horaBrasilia(p.at) < 4));
  if (tarde.length) frases.push(`${tarde.length} ${tarde.length === 1 ? "prato saiu" : "pratos saíram"} depois das 23h.`);
  return frases;
}

export interface EntradaDoResumo {
  periodo: string;
  pratos: readonly PratoDoResumo[];
  fichas: readonly FichaDaTemporada[];
  missoes: readonly MissaoFechada[];
  cozinhas: readonly { id: string; label: string; emoji: string }[];
  /** false = desafio com "ninguém" comenta e dá nota: sem prato do mês por nota. */
  comNotas: boolean;
}

export function montarResumoDoGrupo(e: EntradaDoResumo): ResumoDoGrupo | null {
  const j = janelaDoResumo(e.periodo);
  if (!j) return null;
  const noPeriodo = dentro(j.de, j.ate);
  const pratos = e.pratos.filter((p) => noPeriodo(p.day));
  const missoes = e.missoes.filter((m) => noPeriodo(m.day));
  const porCozinha = new Map<string, number>();
  for (const p of pratos) if (p.cuisineId) porCozinha.set(p.cuisineId, (porCozinha.get(p.cuisineId) ?? 0) + 1);
  const porMissao = new Map<string, { nome: string; emoji: string; n: number }>();
  for (const m of missoes) {
    const c = porMissao.get(m.nome) ?? { nome: m.nome, emoji: m.emoji, n: 0 };
    c.n += 1;
    porMissao.set(m.nome, c);
  }
  const ranking = [...porMissao.values()].sort((a, b) => b.n - a.n);
  return {
    periodo: e.periodo, tipo: j.tipo, de: j.de, ate: j.ate,
    pratos: pratos.length,
    pessoas: new Set(pratos.map((p) => p.uid)).size,
    diasAtivos: new Set(pratos.map((p) => p.day)).size,
    cozinhados: pratos.filter((p) => p.homemade).length,
    comprados: pratos.filter((p) => !p.homemade).length,
    ingrediente: ingredienteDoPeriodo(pratos),
    cozinhas: [...porCozinha.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3)
      .map(([id, n]) => {
        const c = e.cozinhas.find((x) => x.id === id);
        return { id, label: c?.label ?? id, emoji: c?.emoji ?? "🍽️", n };
      }),
    lugar: lugarDoPeriodo(pratos),
    curiosidades: curiosidades(pratos),
    pratoDoPeriodo: melhorPorNota(pratos, e.comNotas),
    missoes: { total: missoes.length, porMissao: ranking.slice(0, 5), preferida: ranking[0]?.nome ?? null },
    podio: placarDoPeriodo(e.fichas, j.de, j.ate).filter((l) => l.pontos > 0).slice(0, 3),
  };
}

export interface EntradaPessoal extends EntradaDoResumo {
  uid: string;
  /** Missões que estavam valendo e não fecharam: o "quase lá". */
  emAberto: readonly { nome: string; emoji: string; feitos: number; need: number }[];
}

export function montarResumoPessoal(e: EntradaPessoal): ResumoPessoal | null {
  const j = janelaDoResumo(e.periodo);
  if (!j) return null;
  const noPeriodo = dentro(j.de, j.ate);
  const meus = e.pratos.filter((p) => p.uid === e.uid && noPeriodo(p.day));
  const placar = placarDoPeriodo(e.fichas, j.de, j.ate);
  const eu = placar.findIndex((l) => l.uid === e.uid);
  const ant = janelaDoResumo(periodoAnterior(e.periodo))!;
  const antes = e.pratos.filter((p) => p.uid === e.uid && dentro(ant.de, ant.ate)(p.day));
  const placarAntes = placarDoPeriodo(e.fichas, ant.de, ant.ate).find((l) => l.uid === e.uid);
  const comPreco = meus.filter((p) => (p.price ?? 0) > 0);
  const melhor = melhorPorNota(meus, e.comNotas)
    ?? (meus.length ? cartao([...meus].sort((a, b) => b.at - a.at)[0]!) : null);
  const quase = [...e.emAberto].filter((m) => m.feitos > 0 && m.feitos < m.need)
    .sort((a, b) => b.feitos / b.need - a.feitos / a.need)[0] ?? null;
  return {
    periodo: e.periodo, uid: e.uid,
    pratos: meus.length,
    pontos: eu >= 0 ? placar[eu]!.pontos : 0,
    posicao: eu + 1,
    ingrediente: ingredienteDoPeriodo(meus),
    casa: meus.filter((p) => p.homemade).length,
    rua: meus.filter((p) => !p.homemade).length,
    gasto: Math.round(comPreco.reduce((s, p) => s + (p.price ?? 0), 0) * 100) / 100,
    comPreco: comPreco.length,
    melhorPrato: melhor,
    missoes: e.missoes.filter((m) => m.uid === e.uid && noPeriodo(m.day))
      .map((m) => ({ nome: m.nome, emoji: m.emoji, bonus: m.bonus })),
    quaseLa: quase,
    anterior: antes.length || (placarAntes?.pontos ?? 0) > 0
      ? { pratos: antes.length, pontos: placarAntes?.pontos ?? 0 } : null,
  };
}
